/* eslint-disable @typescript-eslint/naming-convention */
import { adaptV4Theme, createTheme } from '@mui/material/styles';
import { createColorTokens } from './utils';
// [NOTE]-Mui custom theme
/**
 * Creates a MUI theme based on local UI tokens.
 *
 * @param {Object} arg - The ui tokens.
 * @returns {Object}
 */
export function createWebTheme({ font, colors, colorMap, shape, spacing, typography, breakpoints }) {
    const _palette = createColorTokens(colorMap, colors);
    return createTheme(adaptV4Theme({
        spacing,
        palette: _palette,
        shape,
        typography: {
            // @ts-ignore
            font,
            ...typography
        },
        breakpoints,
        props: {
            MuiButton: {
                variant: 'contained'
            },
            MuiTextField: {
                size: 'small'
            }
        },
        overrides: {
            MuiCheckbox: {
                root: {
                    color: '#8F9294',
                    padding: '6px',
                    '&:hover': {
                        backgroundColor: _palette?.default00 + '04',
                        '&.Mui-checked': {
                            backgroundColor: _palette?.primary10 + '04'
                        }
                    },
                    '&.Mui-checked': {
                        color: _palette?.primary10
                    }
                }
            },
            MuiSwitch: {
                root: {
                    padding: '4px',
                    '& .MuiSwitch-colorPrimary': {
                        '& .MuiSwitch-thumb': {
                            color: '#ffffff !important',
                            transform: 'translate(19%, 16%)',
                            height: '12px',
                            width: '12px'
                        }
                    },
                    '& .MuiSwitch-colorPrimary + .MuiSwitch-track': {
                        backgroundColor: '#D1D1D1 !important',
                        opacity: '1 !important',
                        borderRadius: '10px'
                    },
                    '& .Mui-checked': {
                        '& .MuiSwitch-thumb': {
                            color: '#ffffff !important',
                            transform: 'translate(19%, 16%)',
                            height: '12px',
                            width: '12px'
                        }
                    },
                    '& .Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#CB003D !important',
                        opacity: '1 !important',
                        borderRadius: '10px'
                    },
                }
            },
            MuiButton: {
                root: {
                    fontWeight: 600,
                    textTransform: 'initial',
                    fontSize: '16px',
                    lineHeight: '22px',
                    '&.MuiButton-sizeLarge': {
                        fontSize: '18px',
                        lineHeight: '23px !important',
                        fontWeight: 700
                    },
                    '&.MuiButton-sizeMedium': {
                        lineHeight: '26px',
                    },
                    '&.MuiButton-contained': {
                        backgroundColor: _palette?.primary10,
                        color: '#fff',
                        padding: '7px 16px',
                        '&.MuiButton-sizeLarge': {
                            paddingTop: '12.5px',
                            paddingBottom: '12.5px'
                        },
                        '&:hover': {
                            backgroundColor: _palette?.primary10
                        },
                        '&:disabled': {
                            backgroundColor: '#E0E0E0',
                            color: '#8F9294'
                        }
                    },
                    '&.MuiButton-outlined': {
                        padding: '6px 15px',
                        color: _palette?.outline,
                        borderColor: _palette?.outline,
                        backgroundColor: _palette?.primary10 + '04',
                        '&.MuiButton-sizeLarge': {
                            paddingTop: '11.5px',
                            paddingBottom: '11.5px'
                        },
                        '&:hover': {
                            backgroundColor: _palette?.primary10 + '12'
                        },
                        '&:disabled': {
                            backgroundColor: '#FFFFFF',
                            color: '#8F9294',
                            borderColor: _palette?.default00
                        }
                    }
                }
            },
            MuiTypography: {
                root: {
                    color: _palette?.default00,
                    fontFamily: 'Sarabun'
                }
            },
            MuiFormControlLabel: {
                root: {
                    '&:has(.MuiInputBase-inputSizeSmall)': {
                        '& .MuiFormControlLabel-label': {
                            fontSize: '16px'
                        }
                    },
                    '& .MuiFormControlLabel-label': {
                        whiteSpace: 'nowrap',
                        fontSize: '16px',
                        fontWeight: '500',
                        color: _palette?.default00,
                        fontFamily: 'Sarabun, Tahoma, Arial'
                    },
                    '& .MuiFormControlLabel-asterisk': {
                        color: _palette?.primary10,
                        fontSize: '16px'
                    },
                    '&.MuiFormControlLabel-labelPlacementTop': {
                        margin: 0,
                        width: '100%',
                        alignItems: 'self-start',
                        rowGap: '6px'
                    },
                    '&.MuiFormControlLabel-labelPlacementStart': {
                        margin: 0,
                        '&.form-small': {
                            '& .MuiFormControlLabel-label': {
                                fontSize: '14px',
                                fontFamily: 'Sarabun, Tahoma, Arial'
                            }
                        },
                    },
                    '& .MuiFormHelperText-root': {
                        margin: 0,
                        fontFamily: 'Sarabun, Tahoma, Arial',
                        fontSize: '14px',
                        '&.Mui-error': {
                            color: '#B3261E !important'
                        }
                    }
                }
            },
            MuiTextField: {
                root: {
                    '& .MuiInputBase-input': {
                        paddingTop: '12.5px',
                        paddingBottom: '12.5px',
                        fontSize: '18px',
                        fontWeight: 500,
                        height: 'unset',
                        color: '#44494D',
                        '&::placeholder': {
                            color: '#73777A'
                        },
                        '&.MuiInputBase-inputSizeSmall': {
                            padding: '6.5px 0.875em',
                            fontSize: '16px'
                        }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#8F9294',
                    },
                    '&[data-size="normal"] .MuiInputBase-input': {
                        paddingTop: '6.5px',
                        paddingBottom: '6.5px',
                    }
                }
            },
            MuiOutlinedInput: {
                root: {
                    '&:hover': {
                        border: 'none'
                    },
                    '&.Mui-focused': {
                        border: 'none'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: _palette?.borderHover,
                        borderWidth: '1px'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: _palette?.borderHover,
                        borderWidth: '1px'
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        border: 0
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#E9E9E9',
                        color: '#8F9294'
                    },
                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: _palette?.primary10
                    }
                }
            },
            MuiPaper: {
                root: {
                    borderRadius: '10px',
                    backgroundColor: 'transparent'
                }
            },
            MuiRadio: {
                root: {
                    color: _palette?.default00,
                    '&.Mui-checked': {
                        color: _palette?.primary10
                    }
                }
            },
            MuiFormLabel: {
                root: {
                    color: _palette?.default00,
                    fontWeight: 600,
                    fontFamily: 'Sarabun',
                    '&.Mui-focused': {
                        color: _palette?.default00,
                    },
                    '& .MuiFormLabel-asterisk': {
                        color: _palette?.primary10
                    },
                }
            },
            MuiPopover: {
                root: {
                    '& .MuiPaper-root': {
                        backgroundColor: 'white'
                    }
                }
            },
            MuiButtonBase: {
                root: {
                    '&.Mui-selected': {
                        backgroundColor: `${_palette?.backgroundTextSelect} !important`,
                        color: `${_palette?.textSelect} !important`
                    },
                    '&.MuiMenuItem-root:hover': {
                        backgroundColor: `${_palette?.backgroundTextHover} !important`,
                        color: `${_palette?.textHover} !important`
                    },
                    '&.MuiRadio-root': {
                        '&:hover': {
                            backgroundColor: `${_palette?.backgroundTextHover} !important`,
                        },
                        '&.Mui-disabled': {
                            color: '#DBDCDD !important'
                        }
                    }
                }
            },
            MuiFormControl: {
                root: {
                    height: 'auto',
                    '& .MuiInputBase-root': {
                        border: 'none'
                    },
                    '& .MuiSelect-select': {
                        padding: '6px 0px !important',
                        fontFamily: 'Sarabun',
                        fontSize: '16px',
                        fontWeight: '500'
                    },
                    '& .MuiInputBase-root::placeholder': {
                        color: '#73777A'
                    }
                }
            },
            MuiTableBody: {
                root: {
                    '& .MuiTableRow-root:nth-of-type(even)': {
                        backgroundColor: '#F0F0F0'
                    }
                }
            },
            MuiTableRow: {
                root: {
                    height: '48px',
                    '&.Mui-selected': {
                        backgroundColor: '#FFF5F6 !important'
                    }
                }
            },
            MuiTableCell: {
                root: {
                    padding: '4px 12px',
                    fontSize: '15px',
                    fontFamily: 'Sarabun',
                    '&.MuiTableCell-paddingCheckbox': {
                        padding: 0
                    },
                    '&.MuiTableCell-head': {
                        borderTop: '1px solid #d1d1d1',
                        boxShadow: '0px 3px #d1d1d1',
                        fontWeight: 700,
                        backgroundColor: '#F8EEF0',
                        borderLeft: '1px solid #E0E0E0',
                        borderBottom: 'none !important',
                        fontSize: '16px'
                    },
                    '& .MuiTypography-root': {
                        fontSize: '15px'
                    }
                }
            }
        }
    }));
}
/**
 * Find the first styled ancestor component of an element.
 *
 * @param {HTMLElement|null} target - Element to look up.
 * @param {string} cssClass - Styled component reference.
 * @returns {HTMLElement|null} Ancestor.
 */
export const findAncestorByClass = (target, cssClass) => {
    if (!target || target.classList.contains(cssClass)) {
        return target;
    }
    return findAncestorByClass(target.parentElement, cssClass);
};
/**
 * Checks if the passed element is visible in the viewport.
 *
 * @param {Element} element - The element.
 * @returns {boolean}
 */
export function isElementInTheViewport(element) {
    if (!element) {
        return false;
    }
    if (!document.body.contains(element)) {
        return false;
    }
    const { innerHeight, innerWidth } = window;
    const { bottom, left, right, top } = element.getBoundingClientRect();
    if (bottom <= innerHeight && top >= 0 && left >= 0 && right <= innerWidth) {
        return true;
    }
    return false;
}
const enterKeyElements = ['select', 'textarea', 'summary', 'a'];
/**
 * Informs whether or not the given element does something on its own when pressing the Enter key.
 *
 * This is useful to correctly submit custom made "forms" that are not using the native form element,
 * only when the user is not using an element that needs the enter key to work.
 * Note the implementation is incomplete and should be updated as needed if more complex use cases arise
 * (for example, the Tabs aria pattern is not handled).
 *
 * @param {Element} element - The element.
 * @returns {boolean}
 */
export function operatesWithEnterKey(element) {
    if (enterKeyElements.includes(element.tagName.toLowerCase())) {
        return true;
    }
    if (element.tagName.toLowerCase() === 'button' && element.getAttribute('role') === 'button') {
        return true;
    }
    return false;
}
